<template>
  <div>
    <StepTitle :value="title" class="mb-8" />
    <div>
      <div class="md:flex md:space-x-3">
        <div class="form-group w-full" :class="{ 'form-group-invalid': hasZipErrors }">
          <label for="fast-project-zip" class="px-0.5 text-body-color">{{ zipLabel }}</label>
          <input
            id="fast-project-zip"
            ref="zipInput"
            v-model="zip"
            type="text"
            name="raw_contact_zip"
            class="form-control h-auto w-full rounded-lg py-4"
            placeholder="Code postal"
            autocomplete="postal-code"
            @keyup.tab.prevent="$refs.countrySelect.focus()"
          />
          <div v-if="hasZipErrors" class="invalid-feedback">
            Votre code postal {{ mainStore.errors.user_user_contact_zip | toSentence }}.
          </div>
        </div>
        <div class="form-group w-full" :class="{ 'form-group-invalid': hasZipErrors }">
          <label for="fast-project-country" class="text-body-color">{{ countryLabel }}</label>
          <select
            id="fast-project-country"
            ref="countrySelect"
            v-model="country"
            type="text"
            class="custom-select h-auto w-full rounded-lg py-4"
            name="project[user_user_contact_country]"
            autocomplete="country"
            required
          >
            <option disabled selected value="" class="hidden">Pays</option>
            <option v-for="countryOpt in preferredCountryList" :key="countryOpt.value" :value="countryOpt.value">
              {{ countryOpt.text }}
            </option>
            <option disabled>──────────</option>
            <option v-for="countryOpt in countryList" :key="countryOpt.value" :value="countryOpt.value">
              {{ countryOpt.text }}
            </option>
          </select>
          <div v-if="hasCountryErrors" class="invalid-feedback">
            Votre pays {{ mainStore.errors.user_user_contact_country | toSentence }}.
          </div>
        </div>
      </div>
    </div>

    <div class="mt-6" v-html="mainStore.currentStepContent.reassurance"></div>

    <portal v-if="active" to="fast-footer">
      <StepNav
        :next-disabled="!completed || mainStore.submitting"
        @goToPrev="mainStore.goToPreviousStep()"
        @goToNext="submitProjectRecordAndGoToNextStep"
      />
    </portal>
  </div>
</template>

<script>
import { mapStores } from 'pinia'
import { ALERTS, STEPS, useMainStore } from '@/apps/fast/store'

import { CountryResource } from '@/apps/shared/resources'

export default {
  data: () => ({
    zip: null,
    country: 'FR',
    preferredCountryCodes: ['FR', 'BE', 'CA', 'CH', 'DZ', 'MA', 'MG', 'TN'],
    preferredCountryList: [],
    countryList: []
  }),
  computed: {
    ...mapStores(useMainStore),
    title() {
      return this.mainStore.stepContent(STEPS.location)?.title
    },
    zipLabel() {
      return this.mainStore.stepContent(STEPS.location)?.zipLabel
    },
    hasZipErrors() {
      return this.mainStore.errors.user_user_contact_zip?.length > 0
    },
    hasCountryErrors() {
      return this.mainStore.errors.user_user_contact_country?.length > 0
    },
    countryLabel() {
      return this.mainStore.stepContent(STEPS.location)?.countryLabel
    },
    completed() {
      return this.cleanedZipCode && Boolean(this.country)
    },
    active() {
      return this.mainStore.step === STEPS.location
    },
    cleanedZipCode() {
      return this.zip?.replace(/\s/g, '') ?? ''
    }
  },
  async created() {
    const response = await CountryResource.countries()
    const countryList = response.body

    this.preferredCountryList = this.preferredCountryCodes.map(countryCode =>
      countryList.find(country => country.value === countryCode)
    )
    this.countryList = countryList.filter(country => !this.preferredCountryCodes.includes(country.value))
  },
  methods: {
    proceedByKeyboard(event) {
      if (event.key === 'Enter' && this.completed) {
        this.submitProjectRecordAndGoToNextStep()
      }
    },
    async submitProjectRecordAndGoToNextStep() {
      if (!this.completed) return
      this.mainStore.requestBody.project.user_user_contact_zip = this.cleanedZipCode
      this.mainStore.requestBody.project.user_user_contact_country = this.country
      this.mainStore.submitting = true

      try {
        const user = await this.$root.checkUser(this.mainStore.user.email)
        await this.$root.createAndStoreProject()
        if (user.typology === 'guest') window._paq.push(['trackGoal', 3]) // triggers matomo PDP creation conversion
        if (this.mainStore.user.signedIn || user.typology === 'guest') {
          this.mainStore.goToNextStep()
        } else {
          // Existing user can't have "options" step in order to avoid
          // login during user flow.
          this.$root.sendMatomoPageView('force-skip-options')
          this.mainStore.alert = ALERTS.success
        }
      } catch (error) {
        this.goToFirstErroredStep()
      }
    },
    goToFirstErroredStep() {
      const FIELD_STEP_MAP = {
        name: 'name',
        description: 'description',
        budget: 'budget',
        user_user_contact_email: 'contact',
        user_user_contact_phone: 'contact',
        user_user_contact_country: 'location',
        user_user_contact_zip: 'location'
      }

      const firstErroredStep = FIELD_STEP_MAP[Object.keys(FIELD_STEP_MAP).find(field => this.mainStore.errors[field])]

      if (firstErroredStep) this.mainStore.goToStep(firstErroredStep)
    },
    enter() {
      if (!this.$root.isAndroid()) this.$refs.zipInput.focus()
      this.$root.sendMatomoPageView('location')
    }
  }
}
</script>
